import type { ValueGetterFunc } from "ag-grid-community";

export type PendingCellValue = {
  rowId: string;
  colId: string;
  value: unknown;
} | null;

export type PendingCellValueRef = React.RefObject<PendingCellValue>;

export type WithPendingContext = {
  pendingRef: PendingCellValueRef;
};

export const withPendingValueGetter =
  <TRow extends { rowId: string }, TValue>(
    valueGetter: ValueGetterFunc<TRow, TValue | null>,
  ): ValueGetterFunc<TRow, TValue | null> =>
  (params): TValue | null => {
    if (!params.data) return null;

    // params.context is `any` in AG Grid — WithPendingContext is the exported
    // contract; the runtime check below enforces it for callers that miss it.
    const pendingRef = (params.context as WithPendingContext)?.pendingRef;

    if (!pendingRef) {
      throw new Error(
        "withPendingValueGetter: context must include pendingRef — see WithPendingContext",
      );
    }

    const pending = pendingRef.current;
    const { colId } = params.colDef;

    if (
      pending !== null &&
      colId !== undefined &&
      pending.rowId === params.data.rowId &&
      pending.colId === colId
    ) {
      // pending.value is unknown — stored from CellValueChangedEvent.newValue,
      // which AG Grid types as unknown. Safe to assert: the pending entry is
      // always written from a cell whose value type is TValue.
      return pending.value as TValue;
    }

    return valueGetter(params) ?? null;
  };
